// Regex to identify all HTML tags and text nodes
const matchHtmlTagsRegex = /<\/?[^>]+(>|$)/g // just tags
const matchWordsAndTagsRegex = /<[^>]+>[^\S\r\n]?|([^<\s]+[^\S\r\n]?)/g // word tokens and html tags (including single horizontal trailing whitespace)

/**
 * Normalize and clean a string for matching
 *
 * Handles common discrepancies between backend sentences and HTML content:
 * - Smart quotes/apostrophes → standard ASCII quotes
 * - Various dash types → standard hyphen
 * - Ellipsis variations → standard three dots
 * - HTML entities → decoded characters
 * - Whitespace normalization → single spaces
 * - Legal symbols preservation (§, ¶)
 * - Remove punctuation for fuzzy matching
 *
 * Important: Some tokens (like whitespace-only or punctuation-only) may become
 * empty strings after cleaning. These are filtered out during tokenization to
 * prevent them from breaking sequence matching.
 *
 * @param str - The string to normalize
 * @returns Normalized string ready for token matching, or null if input is null
 */
const matchCleaner = (str: string | null): string | null => {
  if (str == null) {
    return null
  }

  return (
    str
      // Decode common HTML entities first
      .replace(/&quot;/g, '"')
      .replace(/&apos;/g, "'")
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&nbsp;/g, ' ')
      // Normalize smart quotes to standard ASCII quotes
      .replace(/[\u201C\u201D]/g, '"') // " " → "
      .replace(/[\u2018\u2019]/g, "'") // ' ' → '
      // Normalize various dash types to standard hyphen
      .replace(/[\u2013\u2014]/g, '-') // – — → -
      // Normalize ellipsis variations
      .replace(/\u2026/g, '...') // … → ...
      .replace(/\.\s+\.\s+\./g, '...') // . . . → ...
      .replace(/\.\s\.\s\./g, '...') // . . . → ...
      // Normalize whitespace (tabs, newlines, multiple spaces → single space)
      .replace(/\s+/g, ' ')
      // Remove punctuation (but preserve legal symbols like § ¶)
      .replace(/[.,/#!$%^&*;:{}=\-_`~()[\]"'<>]/g, '')
      // Final cleanup
      .trim()
      .toLowerCase()
  )
}

export default function htmlTokenSequenceHighlighter(mainHtmlString: string, highlightHtmlStrings: string[]) {
  // Convert the highlightHtmlStrings into lists of searchList tokens
  const searchLists: string[][] = []
  highlightHtmlStrings.forEach((highlightString) => {
    // Create a flat list with separate tokens for words and HTML tags
    const searchSegments = [...highlightString.matchAll(matchWordsAndTagsRegex)]

    // Create a filtered searchList that does not include HTML tags or tokens that become empty after cleaning
    const searchList = searchSegments
      .map((match) => match[0])
      .filter((segment) => {
        // Skip HTML tags
        if (segment.match(matchHtmlTagsRegex)) {
          return false
        }
        // Skip tokens that become empty/null after cleaning
        const cleaned = matchCleaner(segment)
        return cleaned && cleaned.length > 0
      })

    // Split any searchList greater than X elements into smaller lists.
    // TODO: Opportunity to improve sequence matching by correcting matching errors rather than splitting the list
    const minListSize = 25
    const maxListLength = minListSize * 2 - 1

    // IF the searchList is greater than maxListLength,
    // THEN: split it into smaller lists
    if (searchList.length > maxListLength) {
      const splitSearchLists = splitArray(searchList, minListSize, maxListLength)
      // console.log('Original searchList: ', searchList)
      // console.log('Split searchLists: ', splitSearchLists)

      searchLists.push(...splitSearchLists)
    } else {
      searchLists.push(searchList)
    }
  })

  // HTML Document Tokens
  // Create a flat list with separate tokens for words and HTML tags
  // Create a filtered htmlList that does not include HTML tags or tokens that become empty after cleaning
  // and an indexMap that contains the original index of the item
  const htmlSegments = [...mainHtmlString.matchAll(matchWordsAndTagsRegex)]
  const htmlList = htmlSegments.map((match) => match[0])
  const filteredHtmlList: string[] = []
  const indexMap: number[] = []
  htmlList.forEach((htmlSegment, index) => {
    // Skip HTML tags
    if (htmlSegment.match(matchHtmlTagsRegex)) {
      return
    }

    // Skip tokens that become empty/null after cleaning
    // This prevents empty tokens from breaking sequence matching
    const cleaned = matchCleaner(htmlSegment)
    if (!cleaned || cleaned.length === 0) {
      return
    }

    filteredHtmlList.push(htmlSegment)
    indexMap.push(index)
  })
  // console.log('htmlList: ', htmlList)
  // console.log('filteredHtmlList: ', filteredHtmlList)
  // console.log('indexMap: ', indexMap)

  // If filteredHtmlList is empty, return the original htmlString
  if (filteredHtmlList.length == 0) {
    console.warn('⚠️ HTML Highlighting: Document has no text content to highlight')
    return mainHtmlString
  }

  // HTML Highlight Indices
  // List of list of htmlList element indices that need to be wrapped in a <mark> tag
  // Each matched searchList will generate a list of indices
  const highlightedHtmlListIndices: number[][] = []

  // For each searchList, find the matching sequence in the htmlList
  let failureCount = 0

  searchLists.forEach((searchList): void => {
    // Track how many matches existed before processing this searchList
    const matchCountBefore = highlightedHtmlListIndices.length

    // SEQUENCE MATCHING
    // Match the entire search token sequence with the htmlList stripped of html tags
    //
    // Match Sequence:
    // Look for the sequence of consecutive matches between the searchList and the filteredHtmlList
    // To store the original indices of matching sequences
    const searchListLength = searchList.length
    for (let i = 0; i <= filteredHtmlList.length - searchListLength; i++) {
      let matchFound = true

      for (let j = 0; j < searchListLength; j++) {
        if (matchCleaner(filteredHtmlList[i + j]) != matchCleaner(searchList[j])) {
          matchFound = false
          break
        }
      }

      if (matchFound) {
        // Map back to original indices
        const originalIndices = indexMap.slice(i, i + searchListLength) // Get the original indices of the matching sequence
        highlightedHtmlListIndices.push(originalIndices)
      }
    }

    // Check if THIS specific searchList found any matches
    const matchCountAfter = highlightedHtmlListIndices.length
    const foundMatchForThisSearchList = matchCountAfter > matchCountBefore

    if (!foundMatchForThisSearchList) {
      failureCount++

      // Find closest partial match for Sentry diagnostics
      let bestMatch = { length: 0, startIndex: -1, matchedTokens: 0 }
      for (let i = 0; i <= filteredHtmlList.length - searchList.length; i++) {
        let matchedCount = 0
        for (let j = 0; j < searchList.length; j++) {
          if (matchCleaner(filteredHtmlList[i + j]) === matchCleaner(searchList[j])) {
            matchedCount++
          } else {
            break
          }
        }
        if (matchedCount > bestMatch.matchedTokens) {
          bestMatch = { length: searchList.length, startIndex: i, matchedTokens: matchedCount }
        }
      }

      // Log warning but not to sentry
      console.warn(`⚠️ HTML Highlighting: No matches found for searchList.`)
    }
  })

  // Only log if there were unexpected failures
  if (failureCount > 0) {
    console.warn(`⚠️ HTML Highlighting: ${failureCount}/${searchLists.length} sentence chunks failed to match`)
    console.log(`Expected ${highlightHtmlStrings.length} sentences to highlight, processed as ${searchLists.length} chunks`)
  }

  // After all searchLists have been processed
  // Wrap the matched text elements in <mark> tags for highlighting
  highlightedHtmlListIndices.forEach((group) => {
    // For each group
    // Create an initial anchor tag
    // Then highlight all the elements in the group

    group.forEach((htmlListIndex, thisIndex) => {
      if (thisIndex == 0) {
        htmlList[htmlListIndex] = `<mark class="ref-highlight ref-highlight-start">${htmlList[htmlListIndex]}</mark>`
      } else {
        htmlList[htmlListIndex] = `<mark class="ref-highlight">${htmlList[htmlListIndex]}</mark>`
      }
    })
  })
  // console.log('highlightedHtmlListIndexes: ', highlightedHtmlListIndices)

  // Recombine the htmlList into a string
  const highlightedHtml = htmlList.join('')

  return highlightedHtml
}

/**
 * Split Array
 * @param inputArray
 * @param minSize
 * @param maxListLength
 * @returns
 */
function splitArray<T>(inputArray: T[], minSize: number, maxListLength: number): T[][] {
  if (minSize < 1) {
    throw new Error('Minimum size must be at least 1')
  }

  const result: T[][] = []
  for (let i = 0; i < inputArray.length; i += minSize) {
    // Check if the remaining elements are fewer than maxListLength
    if (inputArray.length - i <= maxListLength) {
      // Add the remaining elements as the last chunk and break the loop
      const lastChunk = inputArray.slice(i)
      result.push(lastChunk)
      break
    }

    // Proceed as normal if there are still more than maxListLength elements remaining
    const chunk = inputArray.slice(i, i + minSize)
    result.push(chunk)
  }

  return result
}
